import { createAuthClient } from "better-auth/react";
import { inferAdditionalFields } from "better-auth/client/plugins";
import type { auth } from "./better-auth";
import { useQuery } from "@tanstack/react-query";
import { getCurrentUserOrganizations } from "@/app/actions/organization.server";

const authClient = createAuthClient({
  baseURL: process.env.NEXT_PUBLIC_BETTER_AUTH_URL || "http://localhost:3001",
  plugins: [inferAdditionalFields<typeof auth>()],
});

export const { signIn, useSession } = authClient;

/**
 * Sign out function that redirects to Cognito logout which will redirect back to clean up local session
 */
export async function signOut() {
  try {
    const response = await fetch("/api/auth/logout");
    if (!response.ok) {
      throw new Error("Failed to get logout URL template");
    }

    const { logoutUrlTemplate } = await response.json();
    const baseUrl = window.location.origin;
    const logoutUrl = `${logoutUrlTemplate}${encodeURIComponent(`${baseUrl}/logout`)}`;

    window.location.assign(logoutUrl);
  } catch (error) {
    console.error("Error getting logout URL:", error);
    // Fallback to local sign-out only
    return authClient.signOut();
  }
}

/**
 * Local sign-out only (without Cognito logout redirect)
 * Used by the sign-out page after Cognito redirect to clean up local session
 */
export function signOutLocal() {
  return authClient.signOut();
}

export function useOrganizations() {
  const { data: session } = useSession();
  const user = session?.user;

  return useQuery({
    queryKey: ["current-user-organizations", user?.id],
    queryFn: () => getCurrentUserOrganizations(),
    enabled: !!user?.id,
  });
}
